<script lang="tsx">
import type { PropType } from 'vue'
import { computed, defineComponent, nextTick, ref, toRefs, watch } from 'vue'
import NP from 'number-precision'
import type { ComponentSize } from 'element-plus'
import { ArrowDown, ArrowUp, Minus, Plus } from '@element-plus/icons-vue'
import { ElButton, ElIcon, ElInput, useFormDisabled, useFormSize } from 'element-plus'
import { isNumber, isString, isUndefined } from '@sjzy/utils'
import { KEYBOARD_KEY, getKeyDownHandler } from '../utils/keyboard'

type StepMethods = 'minus' | 'plus'

const FIRST_DELAY = 800
const SPEED = 150

NP.enableBoundaryChecking(false)
export default defineComponent({
  name: 'SjzyInputNumber',
  props: {
    /**
     * @zh 绑定值
     * @en Value
     */
    modelValue: {
      type: Number as PropType<any>,
    },
    /**
     * @zh 默认值（非受控模式）
     * @en Default value (uncontrolled mode)
     */
    defaultValue: Number,
    /**
     * @zh 模式（`embed`：按钮内嵌模式，`button`：左右按钮模式）
     * @en Mode (`embed`: button embedded mode, `button`: left and right button mode)
     * @values 'embed', 'button'
     */
    mode: {
      type: String as PropType<'embed' | 'button'>,
      default: 'embed',
    },
    /**
     * @zh 数字精度
     * @en Precision
     */
    precision: Number,
    /**
     * @zh 数字变化步长
     * @en Number change step
     */
    step: {
      type: Number,
      default: 1,
    },
    /**
     * @zh 是否禁用
     * @en Whether to disable
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * @zh 是否为错误状态
     * @en Whether it is an error state
     */
    error: {
      type: Boolean,
      default: false,
    },
    /**
     * @zh 最大值
     * @en Max
     */
    max: {
      type: Number,
      default: Number.POSITIVE_INFINITY,
    },
    /**
     * @zh 最小值
     * @en Min
     */
    min: {
      type: Number,
      default: Number.NEGATIVE_INFINITY,
    },
    /**
     * @zh 最大长度
     * @en maxLength
     */
    maxLength: {
      type: Number,
      default: 15,
    },
    /**
     * @zh 定义输入框展示值
     * @en Define the display value of the input
     */
    formatter: {
      type: Function,
    },
    /**
     * @zh 从 `formatter` 转换为数字，和 `formatter` 搭配使用
     * @en Convert from `formatter` to number, and use with `formatter`
     */
    parser: {
      type: Function,
    },
    /**
     * @zh 输入框提示文字
     * @en Input prompt text
     */
    placeholder: String,
    /**
     * @zh 是否隐藏按钮
     * @en Whether to hide the button
     */
    hideButton: {
      type: Boolean,
      default: true,
    },
    /**
     * @zh 输入框大小
     * @en Input size
     * @values 'mini','small','medium','large'
     * @defaultValue 'medium'
     */
    size: {
      type: String as PropType<ComponentSize>,
    },
    /**
     * @zh 是否允许清空输入框
     * @en Whether to allow the input to be cleared
     */
    clearable: {
      type: Boolean,
      default: false,
    },
    /**
     * @zh 触发 `v-model` 的事件
     * @en Trigger event for `v-model`
     */
    modelEvent: {
      type: String as PropType<'change' | 'input'>,
      default: 'input',
    },
    /**
     * @zh 只读
     * @en Readonly
     * @version 2.33.1
     */
    readOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * @zh 内部 input 元素的属性
     * @en Attributes of inner input elements
     * @version 2.52.0
     */
    inputStyle: {
      type: Object,
    },
    /**
     * @description 当min为0时 null
     */
    defaultUndefined: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    'update:modelValue': (_value: number | string | undefined) => true,
    /**
     * @zh 值发生改变时触发
     * @en Triggered when the value changes
     * @param { number | undefined } _value
     * @param {Event} _ev
     */
    'change': (_value: number | undefined, _ev?: Event) => true,
    /**
     * @zh 输入框获取焦点时触发
     * @en Triggered when the input gets focus
     * @param {FocusEvent} _ev
     */
    'focus': (_ev: FocusEvent) => true,
    /**
     * @zh 输入框失去焦点时触发
     * @en Triggered when the input box loses focus
     * @param {FocusEvent} _ev
     */
    'blur': (_ev: FocusEvent) => true,
    /**
     * @zh 用户点击清除按钮时触发
     * @en Triggered when the user clicks the clear button
     * @param {Event} _ev
     * @version 2.23.0
     */
    'clear': (_ev?: Event) => true,
    /**
     * @zh 输入时触发
     * @en Triggered on input
     * @param { number | undefined } __arco__value
     * @param {string} _inputValue
     * @param {Event} _ev
     * @version 2.27.0
     */
    'input': (__arco__value: number | undefined, _inputValue: string, _ev: Event) => true,
  },
  /**
   * @zh 前缀
   * @en Prefix
   * @slot prefix
   */
  /**
   * @zh 后缀
   * @en Suffix
   * @slot suffix
   */
  /**
   * @zh 前置标签
   * @en Prepend
   * @slot prepend
   */
  /**
   * @zh 后置标签
   * @en Append
   * @slot append
   */
  /**
   * @zh 数值增加图标
   * @en Plus
   * @slot plus
   */
  /**
   * @zh 数值减少图标
   * @en Minus
   * @slot minus
   */
  setup(props, { emit, slots }) {
    const { size, disabled } = toRefs(props)
    // const prefixCls = getPrefixCls('input-number')
    const { prefixCls } = useDesign('input-number')

    const inputRef = ref<HTMLInputElement>()
    const _disabled = useFormDisabled(computed(() => disabled.value))
    // const { formItem } = useFormItem()
    const mergedSize = useFormSize(computed(() => size.value))
    const mergedPrecision = computed(() => {
      if (isNumber(props.precision)) {
        const decimal = `${props.step}`.split('.')[1]
        const stepPrecision = (decimal && decimal.length) || 0
        return Math.max(stepPrecision, props.precision)
      }
      return undefined
    })

    const strInNumber = (num: unknown) => {
      return isString(num) && !/^\s*$/.test(num) ? +num : num
    }

    const getStringValue = (number: number | undefined | string) => {
      const num = strInNumber(number)
      if (!isNumber(num))
        return ''

      const numString = mergedPrecision.value ? num.toFixed(mergedPrecision.value) : String(num)
      return props.formatter?.(numString) ?? numString
    }

    // inner input value to display
    const _value = ref(getStringValue(props.modelValue ?? props.defaultValue))

    const valueNumber = computed(() => {
      if (!_value.value)
        return undefined

      const number = Number(props.parser?.(_value.value) ?? _value.value)
      return Number.isNaN(number) ? undefined : number
    })

    // eslint-disable-next-line vue/no-ref-object-reactivity-loss
    const isMin = ref(isNumber(valueNumber.value) && valueNumber.value <= props.min)
    // eslint-disable-next-line vue/no-ref-object-reactivity-loss
    const isMax = ref(isNumber(valueNumber.value) && valueNumber.value >= props.max)

    // 步长重复定时器
    let repeatTimer = 0

    const clearRepeatTimer = () => {
      if (repeatTimer) {
        window.clearTimeout(repeatTimer)
        repeatTimer = 0
      }
    }

    const getLegalValue = (value: number | undefined): number | undefined => {
      if (isUndefined(value))
        return undefined

      if (isNumber(props.min) && value < props.min)
        value = props.min

      if (isNumber(props.max) && value! > props.max)
        value = props.max

      if (props.defaultUndefined && value === 0)
        return undefined

      return isNumber(mergedPrecision.value) ? NP.round(value!, mergedPrecision.value) : value
    }

    const updateNumberStatus = (number: number | undefined) => {
      let _isMin = false
      let _isMax = false
      if (isNumber(number)) {
        if (number <= props.min)
          _isMin = true

        if (number >= props.max)
          _isMax = true
      }
      if (isMax.value !== _isMax)
        isMax.value = _isMax

      if (isMin.value !== _isMin)
        isMin.value = _isMin
    }

    const handleExceedRange = () => {
      const finalValue = getLegalValue(valueNumber.value)
      const stringValue = getStringValue(finalValue)
      if (finalValue !== valueNumber.value || _value.value !== stringValue)
        _value.value = stringValue

      emit('update:modelValue', finalValue)
    }
    watch(
      () => props.min,
      (newVal) => {
        const _isMin = isNumber(valueNumber.value) && valueNumber.value <= newVal
        if (isMin.value !== _isMin)
          isMin.value = _isMin

        const isExceedMinValue = isNumber(valueNumber.value) && valueNumber.value < newVal
        if (isExceedMinValue)
          handleExceedRange()
      },
    )
    watch(
      () => props.max,
      (newVal) => {
        const _isMax = isNumber(valueNumber.value) && valueNumber.value >= newVal
        if (isMax.value !== _isMax)
          isMax.value = _isMax

        const isExceedMaxValue = isNumber(valueNumber.value) && valueNumber.value > newVal
        if (isExceedMaxValue)
          handleExceedRange()
      },
    )

    const nextStep = (method: StepMethods, event: Event) => {
      if (
        _disabled.value
        || (method === 'plus' && isMax.value)
        || (method === 'minus' && isMin.value)
      ) {
        return
      }

      let nextValue: number | undefined
      if (isNumber(valueNumber.value))
        nextValue = getLegalValue(NP[method](valueNumber.value, props.step))
      else nextValue = props.min === Number.NEGATIVE_INFINITY ? 0 : props.min

      _value.value = getStringValue(nextValue)
      updateNumberStatus(nextValue)
      emit('update:modelValue', nextValue)
      emit('change', nextValue, event)
    }

    const handleStepButton = (event: Event, method: StepMethods, needRepeat = false) => {
      event.preventDefault()
      inputRef.value?.focus()

      nextStep(method, event)

      // 长按时持续触发
      if (needRepeat) {
        repeatTimer = window.setTimeout(
          () => (event.target as HTMLElement).dispatchEvent(event),
          repeatTimer ? SPEED : FIRST_DELAY,
        )
      }
    }

    const handleInput = (value: string, ev: Event) => {
      value = value.trim().replace(/。/g, '.')
      value = props.parser?.(value) ?? value

      if (isNumber(Number(value)) || /^(?:\.|-)$/.test(value)) {
        _value.value = props.formatter?.(value) ?? value
        updateNumberStatus(valueNumber.value)
        if (props.modelEvent === 'input')
          emit('update:modelValue', valueNumber.value)

        emit('input', valueNumber.value, _value.value, ev)
      }
    }

    const handleFocus = (ev: FocusEvent) => {
      emit('focus', ev)
    }

    const handleChange = () => {
      const finalValue = getLegalValue(valueNumber.value)
      const stringValue = getStringValue(finalValue)
      if (finalValue !== valueNumber.value || _value.value !== stringValue) {
        _value.value = stringValue
        updateNumberStatus(finalValue)
      }

      nextTick().then(() => {
        if (isNumber(props.modelValue) && props.modelValue !== finalValue) {
          // TODO: verify number
          _value.value = getStringValue(props.modelValue)
          updateNumberStatus(props.modelValue)
        }
      })
      emit('update:modelValue', finalValue)
      emit('change', finalValue)
    }

    const handleBlur = (ev: FocusEvent) => {
      emit('blur', ev)
    }

    const handleClear = async () => {
      _value.value = ''
      emit('update:modelValue', undefined)
      emit('change', undefined)
      emit('clear')
    }

    const onKeyDown = getKeyDownHandler(
      new Map([
        [
          KEYBOARD_KEY.ARROW_UP,
          (ev: Event) => {
            ev.preventDefault()
            !props.readOnly && nextStep('plus', ev)
          },
        ],
        [
          KEYBOARD_KEY.ARROW_DOWN,
          (ev: Event) => {
            ev.preventDefault()
            !props.readOnly && nextStep('minus', ev)
          },
        ],
      ]),
    )

    watch(
      () => props.modelValue,
      (value: number | undefined) => {
        if (value !== valueNumber.value) {
          // TODO: verify number
          _value.value = getStringValue(value)
          updateNumberStatus(value)
        }
      },
    )

    const renderSuffix = () => {
      if (props.readOnly)
        return null

      return (
        <>
          {slots.suffix?.()}
          <div class={`${prefixCls}-step`}>
            <button
              class={[
                `${prefixCls}-step-button`,
                {
                  [`${prefixCls}-step-button-disabled`]: _disabled.value || isMax.value,
                },
              ]}
              type="button"
              tabindex="-1"
              disabled={_disabled.value || isMax.value}
              onMousedown={e => handleStepButton(e, 'plus', true)}
              onMouseup={clearRepeatTimer}
              onMouseleave={clearRepeatTimer}
            >
              {slots.plus
                ? (
                    slots.plus?.()
                  )
                : (
                    <ElIcon size={mergedSize.value}>
                      <ArrowUp />
                    </ElIcon>
                  )}
            </button>
            <button
              class={[
                `${prefixCls}-step-button`,
                {
                  [`${prefixCls}-step-button-disabled`]: _disabled.value || isMin.value,
                },
              ]}
              type="button"
              tabindex="-1"
              disabled={_disabled.value || isMin.value}
              onMousedown={e => handleStepButton(e, 'minus', true)}
              onMouseup={clearRepeatTimer}
              onMouseleave={clearRepeatTimer}
            >
              {slots.minus
                ? (
                    slots.minus?.()
                  )
                : (
                    <ElIcon size={mergedSize.value}>
                      <ArrowDown />
                    </ElIcon>
                  )}
            </button>
          </div>
        </>
      )
    }
    const cls = computed(() => [
      prefixCls,
      `${prefixCls}-mode-${props.mode}`,
      `${prefixCls}-size-${mergedSize.value}`,
      {
        [`${prefixCls}-readonly`]: props.readOnly,
        [`${prefixCls}-clear`]: !props.hideButton,
      },
    ])

    const renderPrependButton = () => {
      return (
        <ElButton
          link
          size={mergedSize.value}
          // @ts-ignore
          tabindex="-1"
          v-slots={{
            icon: () => (
              <ElIcon size={mergedSize.value}>
                <Minus />
              </ElIcon>
            ),
          }}
          class={[`${prefixCls}-step-button`]}
          disabled={_disabled.value || isMin.value}
          // @ts-ignore
          onMousedown={(ev: MouseEvent) => handleStepButton(ev, 'minus', true)}
          onMouseup={clearRepeatTimer}
          onMouseleave={clearRepeatTimer}
        />
      )
    }

    const renderAppendButton = () => {
      return (
        <ElButton
          link
          size={mergedSize.value}
          // @ts-ignore
          tabindex="-1"
          v-slots={{
            icon: () => (
              <ElIcon size={mergedSize.value}>
                <Plus />
              </ElIcon>
            ),
          }}
          class={[`${prefixCls}-step-button`]}
          disabled={_disabled.value || isMax.value}
          // @ts-ignore
          onMousedown={(ev: MouseEvent) => handleStepButton(ev, 'plus', true)}
          onMouseup={clearRepeatTimer}
          onMouseleave={clearRepeatTimer}
        />
      )
    }

    const render = () => {
      const _slots
        = props.mode === 'embed'
          ? {
              prepend: slots.prepend,
              prefix: slots.prefix,
              suffix: props.hideButton ? slots.suffix : renderSuffix,
              append: slots.append,
            }
          : {
              prepend: props.hideButton ? slots.prepend : renderPrependButton,
              prefix: slots.prefix,
              suffix: slots.suffix,
              append: props.hideButton ? slots.append : renderAppendButton,
            }

      return (
        <ElInput
          key={`__sjzy__${props.mode}`}
          v-slots={_slots}
          ref={inputRef}
          class={cls.value}
          type="text"
          clearable={props.clearable}
          size={mergedSize.value}
          v-model={_value.value}
          placeholder={props.placeholder}
          disabled={_disabled.value}
          readonly={props.readOnly}
          inputStyle={props.inputStyle}
          onInput={handleInput as any}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onClear={handleClear as any}
          onChange={handleChange as any}
          // @ts-ignore
          onKeydown={onKeyDown}
          maxlength={props.maxLength}
        />
      )
    }

    return {
      inputRef,
      render,
    }
  },
  methods: {
    /**
     * @zh 使输入框获取焦点
     * @en Make the input box focus
     * @public
     */
    focus() {
      ;(this.inputRef as HTMLInputElement)?.focus()
    },
    /**
     * @zh 使输入框失去焦点
     * @en Make the input box lose focus
     * @public
     */
    blur() {
      ;(this.inputRef as HTMLInputElement)?.blur()
    },
  },
  render() {
    return this.render()
  },
})
</script>

<style lang="scss" scoped>
$input-prefix-cls: '#{$base-name}-input';
$input-number-prefix-cls: '#{$base-name}-input-number';

%transition {
  transition: all 0.1 cubic-bezier(0, 0, 1, 1);
}

:deep() {
  .el-input-group__prepend,
  .el-input-group__append {
    padding: 0 16px !important;

    &:hover {
      background-color: var(--el-fill-color-light) !important;
      border-color: var(--el-color-primary);
    }
  }
}

.#{$input-number-prefix-cls} {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  border-radius: var(--el-border-radius-base);

  @extend %transition;

  &-clear {
    :deep(.el-input__clear) {
      margin-right: 33px !important;
    }
  }

  &-step-button {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px !important;
    padding: 0 !important;
    cursor: pointer;
    user-select: none;
    background-color: var(--el-fill-color-light);

    @extend %transition;

    &:hover {
      background-color: var(--el-fill-color-light) !important;
      border-color: var(--el-fill-color-light) !important;
    }

    &:disabled {
      cursor: not-allowed;

      &:hover,
      &:active {
        background-color: var(--el-fill-color-light);
        border-color: var(--el-color-primary);
      }
    }
  }

  &-prefix,
  &-suffix {
    @extend %transition;
  }

  // embed mode
  &-mode-embed {
    .#{$input-number-prefix-cls}-step {
      position: absolute;
      top: 1px;
      right: 1px;
      bottom: 1px;
      overflow: hidden;
      color: var(--color);
      border-left: var(--el-border);
      border-radius: 0 var(--el-border-radius-base) 0 0;
      opacity: 0;

      @extend %transition;
      .#{$input-number-prefix-cls}-step-button {
        width: 100%;
        height: 50%;
        font-size: 10px;
        border: none;
        border-color: var(--el-color-primary);

        &:first-child {
          border-bottom: var(--el-border);
        }
      }
    }

    .#{$input-prefix-cls}-suffix {
      justify-content: flex-end;
      min-width: 6px;
    }

    .#{$input-prefix-cls}-suffix-has-feedback {
      min-width: 32px;

      .#{$input-number-prefix-cls}-step {
        right: 30px;
      }
    }

    &:not(.#{$input-prefix-cls}-disabled):not(.#{$input-prefix-cls}-outer-disabled) {
      &:hover,
      &:focus-within {
        & .#{$input-number-prefix-cls}-step {
          opacity: 1;

          & ~ .#{$input-prefix-cls}-suffix {
            pointer-events: none;
            opacity: 0;
          }
        }
      }
    }

    // 修正 InputNumber 未激活 hover 时 StepButton 的 hover 背景色
    &.#{$input-prefix-cls}-wrapper:not(.#{$input-prefix-cls}-focus) {
      .#{$input-number-prefix-cls}-step-button:not(
          .#{$input-number-prefix-cls}-step-button-disabled
        ):hover {
        background-color: var(--el-fill-color-light);
      }
    }
  }

  // button mode
}
</style>
